import React from 'react';
import {
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer,
  Tooltip,
  Legend,
} from 'recharts';
import { CATEGORY_LABELS, CATEGORY_COLORS } from '@/types/transaction';
import type { CategorySummary, TransactionCategory } from '@/types/transaction';

// Generate consistent color from string (same string = same color)
function stringToColor(str: string): string {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    hash = str.charCodeAt(i) + ((hash << 5) - hash);
  }
  const hue = Math.abs(hash % 360);
  return `hsl(${hue}, 70%, 50%)`;
}

// Get color for category with fallback to generated color
export function getCategoryColor(category: TransactionCategory | string): string {
  return CATEGORY_COLORS[category as TransactionCategory] || stringToColor(category);
}

interface CategoryPieChartProps {
  data: CategorySummary[];
  height?: number;
  showLegend?: boolean;
}

const formatCurrency = (value: number) => {
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
  }).format(value);
};

interface CustomTooltipProps {
  active?: boolean;
  payload?: Array<{
    payload: CategorySummary & { name: string; fill: string };
  }>;
}

const CustomTooltip: React.FC<CustomTooltipProps> = ({ active, payload }) => {
  if (active && payload && payload.length) {
    const data = payload[0].payload;
    return (
      <div className="bg-white dark:bg-secondary-800 shadow-lg rounded-lg p-3 border border-gray-200 dark:border-secondary-700">
        <p className="font-medium text-gray-900 dark:text-white">
          {CATEGORY_LABELS[data.category]}
        </p>
        <p className="text-sm text-gray-600 dark:text-gray-400">
          {formatCurrency(data.amount)}
        </p>
        <p className="text-xs text-gray-500 dark:text-gray-500">
          {data.percentage.toFixed(1)}% - {data.count} transacoes
        </p>
      </div>
    );
  }
  return null;
};

interface CustomLegendProps {
  payload?: Array<{
    value: string;
    color: string;
    payload: CategorySummary & { name: string };
  }>;
}

const CustomLegend: React.FC<CustomLegendProps> = ({ payload }) => {
  if (!payload) return null;

  return (
    <div className="flex flex-wrap justify-center gap-3 mt-4">
      {payload.map((entry, index) => (
        <div key={index} className="flex items-center gap-1.5">
          <div
            className="w-3 h-3 rounded-full"
            style={{ backgroundColor: entry.color }}
          />
          <span className="text-xs text-gray-600 dark:text-gray-400">
            {entry.value}
          </span>
        </div>
      ))}
    </div>
  );
};

export const CategoryPieChart: React.FC<CategoryPieChartProps> = ({
  data,
  height = 300,
  showLegend = true,
}) => {
  // Transform data for Recharts (with fallback for unknown categories)
  const chartData = data.map((item) => ({
    ...item,
    name: CATEGORY_LABELS[item.category] || item.category || 'Outros',
    fill: getCategoryColor(item.category),
  }));

  if (data.length === 0) {
    return (
      <div
        className="flex items-center justify-center text-gray-500 dark:text-gray-400"
        style={{ height }}
      >
        Sem dados para exibir
      </div>
    );
  }

  return (
    <div style={{ height }}>
      <ResponsiveContainer width="100%" height="100%">
        <PieChart>
          <Pie
            data={chartData}
            cx="50%"
            cy="50%"
            innerRadius={60}
            outerRadius={90}
            paddingAngle={2}
            dataKey="amount"
            nameKey="name"
            animationBegin={0}
            animationDuration={800}
          >
            {chartData.map((entry, index) => (
              <Cell
                key={`cell-${index}`}
                fill={entry.fill}
                stroke="transparent"
              />
            ))}
          </Pie>
          <Tooltip content={<CustomTooltip />} />
          {showLegend && <Legend content={<CustomLegend />} />}
        </PieChart>
      </ResponsiveContainer>
    </div>
  );
};

// Simple version without Recharts for cases where the library might not load
export const CategoryPieChartSimple: React.FC<CategoryPieChartProps> = ({
  data,
}) => {
  return (
    <div className="space-y-3">
      {data.map((item) => (
        <div key={item.category} className="flex items-center gap-3">
          <div
            className="w-4 h-4 rounded-full flex-shrink-0"
            style={{ backgroundColor: getCategoryColor(item.category) }}
          />
          <div className="flex-1 min-w-0">
            <div className="flex justify-between items-center">
              <span className="text-sm font-medium text-gray-700 dark:text-gray-300 truncate">
                {CATEGORY_LABELS[item.category] || item.category || 'Outros'}
              </span>
              <span className="text-sm text-gray-500 dark:text-gray-400 ml-2">
                {item.percentage.toFixed(1)}%
              </span>
            </div>
            <div className="w-full bg-gray-200 dark:bg-secondary-700 rounded-full h-1.5 mt-1">
              <div
                className="h-1.5 rounded-full transition-all duration-500"
                style={{
                  width: `${item.percentage}%`,
                  backgroundColor: getCategoryColor(item.category),
                }}
              />
            </div>
          </div>
          <span className="text-sm font-medium text-gray-900 dark:text-white whitespace-nowrap">
            {formatCurrency(item.amount)}
          </span>
        </div>
      ))}
    </div>
  );
};

export default CategoryPieChart;
